import type { AssistantConversationSummary } from "@cloudserver/contracts";

/*
 * Where the chat opens (docs/assistant.md, "Conversations"): the last
 * conversation continues when it is about the current project (or the
 * whole organisation outside projects) and had a message in the last two
 * hours, and the page joins its context; otherwise a new conversation
 * about the current page starts, the previous one a click away.
 */

export const CONTINUE_WITHIN_MS = 2 * 60 * 60 * 1000;

export type Opening = { kind: "continue"; id: string } | { kind: "new" };

export function whereToOpen(
  conversations: readonly Pick<
    AssistantConversationSummary,
    "id" | "projectId" | "lastActivityAt"
  >[],
  projectId: string | null,
  now: number,
): Opening {
  const last = [...conversations].sort((a, b) =>
    b.lastActivityAt.localeCompare(a.lastActivityAt),
  )[0];
  if (!last || last.projectId !== projectId) return { kind: "new" };
  return now - Date.parse(last.lastActivityAt) <= CONTINUE_WITHIN_MS
    ? { kind: "continue", id: last.id }
    : { kind: "new" };
}

export interface ConversationGroup {
  /** Null: «Вся организация». */
  projectId: string | null;
  projectName: string | null;
  items: AssistantConversationSummary[];
}

/**
 * The switcher's list: the current project's conversations first, then the
 * other projects', then the organisation's; newest first in each, filtered
 * by a search over titles and chips.
 */
export function groupConversations(
  conversations: readonly AssistantConversationSummary[],
  currentProjectId: string | null,
  search = "",
): ConversationGroup[] {
  const needle = search.trim().toLowerCase();
  const matches = (c: AssistantConversationSummary) =>
    needle === "" ||
    c.title.toLowerCase().includes(needle) ||
    c.context.some((chip) => chip.label.toLowerCase().includes(needle));
  const groups = new Map<string, ConversationGroup>();
  for (const c of [...conversations].sort((a, b) =>
    b.lastActivityAt.localeCompare(a.lastActivityAt),
  )) {
    if (!matches(c)) continue;
    const key = c.projectId ?? "";
    const group = groups.get(key) ?? {
      projectId: c.projectId,
      projectName: c.projectName,
      items: [],
    };
    group.items.push(c);
    groups.set(key, group);
  }
  const rank = (g: ConversationGroup) =>
    g.projectId !== null && g.projectId === currentProjectId ? 0 : g.projectId === null ? 2 : 1;
  return [...groups.values()].sort(
    (a, b) => rank(a) - rank(b) || (a.projectName ?? "").localeCompare(b.projectName ?? ""),
  );
}
